"use client";

import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { classNames } from "../ui/class-names";

export type PresetSelectOption<T extends string> = {
  value: T;
  label: string;
};

export type PresetSelectProps<T extends string> = Readonly<{
  value: T;
  options: readonly PresetSelectOption<T>[];
  onChange: (value: T) => void;
  "aria-label": string;
  "aria-describedby"?: string;
  idPrefix: string;
  /** White bordered field chrome when nested in FontAndSizeControl. */
  field?: boolean;
}>;

export function PresetSelect<T extends string>({
  value,
  options,
  onChange,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  idPrefix,
  field = false,
}: PresetSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties | null>(
    null,
  );
  const current = options.find((option) => option.value === value) ?? options[0];
  const listboxId = `${idPrefix}-listbox`;

  function getDropdownStyle(): CSSProperties | null {
    const button = buttonRef.current;

    if (!button) {
      return null;
    }

    const rect = button.getBoundingClientRect();
    const margin = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.max(rect.width, 12 * 16);
    const left = Math.min(
      Math.max(rect.left, margin),
      viewportWidth - width - margin,
    );
    const top = rect.bottom + 4;
    const maxHeight = Math.max(16 * 8, viewportHeight - top - margin);

    return {
      position: "fixed",
      left,
      top,
      width,
      maxHeight,
    };
  }

  function close() {
    setIsOpen(false);
    setDropdownStyle(null);
  }

  function open() {
    setActiveIndex(Math.max(0, options.findIndex((o) => o.value === value)));
    setDropdownStyle(getDropdownStyle());
    setIsOpen(true);
  }

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      if (
        listboxRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }

      close();
    }

    function reposition(event?: Event) {
      const target = event?.target;
      if (
        target instanceof Node &&
        listboxRef.current &&
        (target === listboxRef.current || listboxRef.current.contains(target))
      ) {
        return;
      }

      setDropdownStyle(getDropdownStyle());
    }

    document.addEventListener("mousedown", handleClickOutside);
    // Capture scroll from any ancestor so the portaled list stays on the trigger.
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    window.visualViewport?.addEventListener("resize", reposition);
    window.visualViewport?.addEventListener("scroll", reposition);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      window.visualViewport?.removeEventListener("resize", reposition);
      window.visualViewport?.removeEventListener("scroll", reposition);
    };
  }, [isOpen]);

  function select(option: PresetSelectOption<T>) {
    onChange(option.value);
    close();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (!isOpen) {
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        open();
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, options.length - 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const option = options[activeIndex];
      if (option) {
        select(option);
      }
      return;
    }

    if (event.key === "Escape") {
      close();
    }
  }

  return (
    <div className={classNames("relative", field ? "w-full min-w-0" : "w-full")}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        title={ariaLabel}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleKeyDown}
        className={classNames(
          "inline-flex w-full min-w-0 items-center justify-between gap-1.5 text-xs font-medium text-zinc-950 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
          field
            ? "rounded-md border border-zinc-200 bg-white px-2.5 py-2 hover:border-zinc-300"
            : "rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 hover:bg-zinc-100",
        )}
      >
        <span className="truncate font-semibold">{current?.label}</span>
        <ChevronDown
          aria-hidden="true"
          className={classNames(
            "size-4 shrink-0 text-zinc-500 transition",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && dropdownStyle
        ? createPortal(
            <ul
              ref={listboxRef}
              id={listboxId}
              role="listbox"
              data-document-settings-dropdown=""
              className="z-50 overflow-auto rounded-md border border-zinc-200 bg-white py-1 shadow-lg"
              style={dropdownStyle}
              onMouseDown={(event) => {
                // Keep parent settings popovers from treating this portal as an outside click.
                event.stopPropagation();
              }}
            >
              {options.map((option, index) => (
                <li
                  key={option.value}
                  id={`${idPrefix}-option-${option.value}`}
                  role="option"
                  aria-selected={option.value === value}
                  onClick={() => select(option)}
                  className={classNames(
                    "cursor-pointer px-3 py-2 text-sm",
                    index === activeIndex
                      ? "bg-zinc-100 text-zinc-950"
                      : "text-zinc-700",
                    option.value === value && "font-semibold",
                  )}
                >
                  {option.label}
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
